import { useAppSelector } from "./reduxHooks";
import type { OpenDocument } from "../components/deals/WorkspaceTabBar";
import { useFeatureFlag } from "./useFeatureFlag";
import { selectOpenDocuments, selectActiveDocumentId } from "../DocumentPreviewSlice_Store";
import { selectLayoutMode } from "../LayoutSlice_Store";
import { selectHasWorkspaceResults } from "../ResultsSlice_Store";
import { selectCurrentProjectType } from "../ProjectSlice_Store";
import { selectSelectedUseCase } from "../ChatSlice_Store";
import { selectExpertOpenSequence, selectSelectedExpert } from "../ExpertsSlice_Store";
import {
  selectSelectedDealId,
  selectSelectedDealTab,
  selectDealDetail,
} from "../DealsTableSlice_Store";

export interface DealSelection {
  inlineDealsFlag: boolean;
  selectedDealId: string | null;
  selectedDealTab: string;
  dealName: string;
  hasDealSelected: boolean;
  dealDocsCount: number;
}

/** The inline-deals flag plus everything about the currently selected deal. */
export function useDealSelection(): DealSelection {
  const inlineDealsFlag = useFeatureFlag("inlineDeals");
  const selectedDealId = useAppSelector(selectSelectedDealId);
  const selectedDealTab = useAppSelector(selectSelectedDealTab);
  const selectedDealData = useAppSelector((state) => selectDealDetail(state, selectedDealId));

  // Count docs for the selected deal from documentRegistry
  // (the deal API doesn't return documents)
  const dealDocsCount = useAppSelector((state) => {
    if (!selectedDealId) return 0;
    const registry = state.resultsSlice?.documentRegistry || [];
    return registry.filter((doc: { dealId?: string }) => doc.dealId === selectedDealId).length;
  });

  return {
    inlineDealsFlag,
    selectedDealId,
    selectedDealTab,
    dealName: selectedDealData?.dealName || "Deal",
    hasDealSelected: inlineDealsFlag && !!selectedDealId,
    dealDocsCount,
  };
}

export interface ExpertSelection {
  expertName: string;
  hasExpertSelected: boolean;
  /** Bumped on every expert open, so a repeat selection still switches tabs. */
  openSequence: number;
}

/** The currently open expert profile, gated by the inline-deals flag. */
export function useExpertSelection(inlineDealsFlag: boolean): ExpertSelection {
  const expert = useAppSelector(selectSelectedExpert);
  const openSequence = useAppSelector(selectExpertOpenSequence);
  return {
    expertName: expert?.personDisplayName || "Expert",
    hasExpertSelected: inlineDealsFlag && !!expert,
    openSequence,
  };
}

export interface WorkspaceResults {
  openDocuments: OpenDocument[];
  activeDocumentId: string | null;
  layoutMode: string;
  hasWorkspaceResults: boolean;
  effectiveUseCase: string | null;
  dealCount: number;
  documentCount: number;
  hasResults: boolean;
  showWorkspace: boolean;
}

/** Whether the workspace should be on screen, and what it has to show. */
export function useWorkspaceResults(
  inlineDealsFlag: boolean,
  hasDealSelected: boolean,
  hasExpertSelected: boolean
): WorkspaceResults {
  const openDocuments = useAppSelector(selectOpenDocuments);
  const activeDocumentId = useAppSelector(selectActiveDocumentId);
  const layoutMode = useAppSelector(selectLayoutMode);
  const hasWorkspaceResults = useAppSelector(selectHasWorkspaceResults);
  const deals = useAppSelector((state) => state.resultsSlice?.deals || []);
  const documents = useAppSelector((state) => state.resultsSlice?.documents || []);

  // Use case comes from two sources:
  // - selectedUseCase: set when the user clicks a conversation starter (new project)
  // - projectType: loaded from the backend PROJECTS table (reloaded project)
  const selectedUseCase = useAppSelector(selectSelectedUseCase);
  const projectType = useAppSelector(selectCurrentProjectType);

  // When the inline flag is on, top-level deals/documents shouldn't force the
  // workspace open — it opens only from explicit actions (deal click, doc
  // preview, facts dispatch).
  const hasResults = inlineDealsFlag
    ? openDocuments.length > 0 || hasDealSelected || hasExpertSelected
    : deals.length > 0 || documents.length > 0 || openDocuments.length > 0;

  return {
    openDocuments,
    activeDocumentId,
    layoutMode,
    hasWorkspaceResults,
    effectiveUseCase: selectedUseCase || projectType,
    dealCount: deals.length,
    documentCount: documents.length,
    hasResults,
    // layoutMode === 'workspace' keeps the panel visible even when results are
    // empty (e.g. a search returned 0 deals — show the empty-state table
    // rather than collapsing).
    showWorkspace: layoutMode === "workspace" || hasResults,
  };
}
